/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import * as d3 from "d3";
import {
  AdsrCurve,
  type AdsrEnvelope,
  AdsrMovePoint,
  AdsrPoints,
  type AdsrRamp,
  type AdsrStage,
} from "@wbaudio/utils";
import { CreateChart } from "../../core/chart.ts";
import { type ChartMode, EditorSize, ModeSize, ModeTheme, PadMargin } from "../../core/mode.ts";
import { useChart, useLatest } from "../../core/useChart.ts";
import { RefLineLayer } from "../../layers/refLine.ts";
import { AreaLayer } from "../../layers/areas.ts";
import { LinesLayer } from "../../layers/lines.ts";
import { type HandlePosition, HandlesLayer } from "../../layers/handles.ts";
import { GridLayer } from "../../layers/grid.ts";
import { ChartPalette } from "../../utils/theme.ts";

export const AdsrEditorSize = EditorSize(10, 4);
const AdsrEditorPad = 14;

function CreateAdsrChart(
  container: HTMLElement,
  mode: ChartMode,
  onDrag: (id: string, position: HandlePosition) => void,
) {
  const editor = mode === "editor";
  const size = ModeSize(mode, AdsrEditorSize);
  const fill = AreaLayer();
  const curve = LinesLayer();
  const handles = editor ? HandlesLayer({ onDrag }) : null;
  const chart = CreateChart(container, {
    width: size.width,
    height: size.height,
    margin: PadMargin(editor ? AdsrEditorPad : size.pad),
    x: d3.scaleLinear().domain([0, 1]),
    y: d3.scaleLinear().domain([0, 1]),
    theme: ModeTheme(mode),
    layers: [
      ...(editor
        ? [
          GridLayer({ axis: "x", ticks: 4 }),
          RefLineLayer({ axis: "y", value: 0, dashed: false }),
          RefLineLayer({ axis: "x", value: 0, dashed: false }),
          fill,
        ]
        : []),
      curve,
      ...(handles ? [handles] : []),
    ],
  });
  return { chart, fill, curve, handles, destroy: chart.destroy };
}

const StageLabels: Record<AdsrStage, string> = {
  attack: "A",
  decay: "D",
  sustain: "S",
  release: "R",
};

function useAdsrChart(value: AdsrEnvelope, mode: ChartMode, onChange?: (v: AdsrEnvelope) => void) {
  const latest = useLatest({ value, onChange });
  const { ref, chart } = useChart(
    (container) =>
      CreateAdsrChart(container, mode, (id, p) => {
        const { value, onChange } = latest.current;
        onChange?.(AdsrMovePoint(value, id as AdsrStage, p.x, p.y));
      }),
    [mode],
  );

  useEffect(() => {
    if (!chart) return;
    const points = AdsrCurve(value).map((p) => ({ x: p.t, y: p.v }));
    chart.fill.set([{ id: "fill", points }]);
    chart.curve.set([{ id: "envelope", points }]);
    chart.handles?.set(
      AdsrPoints(value).map((p) => ({
        id: p.stage,
        x: p.t,
        y: p.v,
        radius: 5,
        axis: p.stage === "release" ? "x" : "xy",
        label: StageLabels[p.stage],
        labelColor: ChartPalette.grey,
      })),
    );
    chart.chart.setDomain([0, value.maxTime]);
  }, [chart, value]);

  return ref;
}

export interface AdsrEditorProps {
  value: AdsrEnvelope;
  onChange: (v: AdsrEnvelope) => void;
}

const SelectStyle = {
  fontSize: 10,
  background: "#222",
  color: "#ccc",
  border: "1px solid #444",
  borderRadius: 2,
  padding: "1px 2px",
};

export function AdsrEditor(props: AdsrEditorProps) {
  const ref = useAdsrChart(props.value, "editor", props.onChange);
  const { value, onChange } = props;
  const stages: AdsrStage[] = value.sustain.ramp === undefined
    ? ["attack", "decay", "release"]
    : ["attack", "decay", "sustain", "release"];

  return (
    <div style={{ padding: "6px 4px 8px" }}>
      <div ref={ref} />
      <div style={{ display: "flex", gap: 6, marginTop: 5, paddingLeft: AdsrEditorPad }}>
        {stages.map((stage) => (
          <label
            key={stage}
            style={{
              fontSize: 10,
              color: ChartPalette.grey,
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            {StageLabels[stage]}:
            <select
              value={value[stage].ramp ?? "linear"}
              style={SelectStyle}
              onChange={(e) => {
                const ramp = (e.target as HTMLSelectElement).value as AdsrRamp;
                onChange({ ...value, [stage]: { ...value[stage], ramp } });
              }}
            >
              <option value="linear">linear</option>
              <option value="exponential">exp</option>
            </select>
          </label>
        ))}
      </div>
    </div>
  );
}

export interface AdsrInlineProps {
  value: AdsrEnvelope;
}

export function AdsrInline(props: AdsrInlineProps) {
  const ref = useAdsrChart(props.value, "inline");
  return <div ref={ref} />;
}
